"use client"

import type { HttpTypes } from "@medusajs/types"
import { useEffect, useMemo, useRef, useState, type TouchEvent } from "react"
import styles from "./product-gallery.module.css"

export type ProductGalleryImage = Pick<HttpTypes.StoreProductImage, "id" | "url">

export type ProductGalleryProps = {
  images: ProductGalleryImage[]
  productTitle: string
  selectedVariantImage?: ProductGalleryImage | null
}

type TouchState = {
  startX: number
  startY: number
  lastX: number
  lastY: number
  axis: "x" | "y" | null
} | null

const AXIS_LOCK_PX = 8
const SWIPE_PX = 50

const reducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches

const reorderSelectedImageFirst = (
  images: ProductGalleryImage[],
  selectedVariantImage?: ProductGalleryImage | null
) => {
  if (!selectedVariantImage?.id && !selectedVariantImage?.url) return images
  const selectedIndex = images.findIndex(
    (image) =>
      (selectedVariantImage.id && image.id === selectedVariantImage.id) ||
      image.url === selectedVariantImage.url
  )
  if (selectedIndex <= 0) return images
  const reordered = [...images]
  const [selected] = reordered.splice(selectedIndex, 1)
  reordered.unshift(selected)
  return reordered
}

export default function ProductGallery({
  images,
  productTitle,
  selectedVariantImage,
}: ProductGalleryProps) {
  const sortedImages = useMemo(
    () => reorderSelectedImageFirst(images, selectedVariantImage),
    [images, selectedVariantImage?.id, selectedVariantImage?.url]
  )
  const [currentIndex, setCurrentIndex] = useState(0)
  const thumbnailRefs = useRef<Array<HTMLButtonElement | null>>([])
  const touchRef = useRef<TouchState>(null)

  useEffect(() => {
    setCurrentIndex(0)
  }, [selectedVariantImage?.id, selectedVariantImage?.url])

  useEffect(() => {
    setCurrentIndex((current) => Math.min(current, Math.max(0, sortedImages.length - 1)))
  }, [sortedImages.length])

  useEffect(() => {
    if (currentIndex === 0) return
    const thumbnail = thumbnailRefs.current[currentIndex]
    if (!thumbnail || typeof thumbnail.scrollIntoView !== "function") return

    thumbnail.scrollIntoView({
      behavior: reducedMotion() ? "auto" : "smooth",
      block: "nearest",
      inline: "center",
    })
  }, [currentIndex])

  if (!sortedImages.length) {
    return <div className={styles.gallery} data-product-order="gallery" data-testid="product-gallery-empty" />
  }

  const showImage = (index: number) => {
    if (index < 0 || index >= sortedImages.length) return
    setCurrentIndex(index)
  }

  const onTouchStart = (event: TouchEvent<HTMLDivElement>) => {
    const point = event.touches[0]
    if (!point) return
    touchRef.current = {
      startX: point.clientX,
      startY: point.clientY,
      lastX: point.clientX,
      lastY: point.clientY,
      axis: null,
    }
  }

  const onTouchMove = (event: TouchEvent<HTMLDivElement>) => {
    const state = touchRef.current
    const point = event.touches[0]
    if (!state || !point) return
    state.lastX = point.clientX
    state.lastY = point.clientY
    const dx = point.clientX - state.startX
    const dy = point.clientY - state.startY
    if (!state.axis) {
      if (Math.max(Math.abs(dx), Math.abs(dy)) < AXIS_LOCK_PX) return
      state.axis = Math.abs(dx) > Math.abs(dy) ? "x" : "y"
    }
    if (state.axis === "x") event.preventDefault()
  }

  const onTouchEnd = (event: TouchEvent<HTMLDivElement>) => {
    const state = touchRef.current
    if (!state) return
    const point = event.changedTouches[0]
    if (point) {
      state.lastX = point.clientX
      state.lastY = point.clientY
    }
    touchRef.current = null
    if (state.axis !== "x") return
    const dx = state.lastX - state.startX
    if (Math.abs(dx) < SWIPE_PX) return
    setCurrentIndex((current) =>
      dx < 0
        ? Math.min(sortedImages.length - 1, current + 1)
        : Math.max(0, current - 1)
    )
  }

  return (
    <div className={styles.gallery} data-product-order="gallery" data-active-index={currentIndex}>
      <div
        className={styles.mainImageContainer}
        data-testid="product-gallery-viewport"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={() => { touchRef.current = null }}
      >
        <div
          className={styles.imagesTrack}
          data-testid="product-gallery-track"
          data-active-index={currentIndex}
          style={{ transform: `translateX(-${currentIndex * 100}%)` }}
        >
          {sortedImages.map((image, index) => (
            <div
              key={image.id ?? image.url}
              className={styles.imageSlide}
              data-testid="product-gallery-slide"
            >
              <img
                src={image.url}
                alt={`${productTitle} görsel ${index + 1}`}
                className={styles.productImage}
                loading={index === 0 ? "eager" : "lazy"}
                draggable={false}
              />
            </div>
          ))}
        </div>
      </div>

      {sortedImages.length > 1 ? (
        <div className={styles.thumbnailsWrapper} aria-label="Ürün küçük görselleri">
          {sortedImages.map((image, index) => (
            <button
              key={image.id ?? image.url}
              ref={(element) => { thumbnailRefs.current[index] = element }}
              type="button"
              className={`${styles.thumbnail} ${index === currentIndex ? styles.thumbnailActive : ""}`}
              aria-label={`${productTitle} ${index + 1}. görseli göster`}
              aria-current={index === currentIndex ? "true" : undefined}
              onClick={() => showImage(index)}
            >
              <img
                src={image.url}
                alt={`${productTitle} küçük görsel ${index + 1}`}
                className={styles.thumbnailImage}
                loading="lazy"
                draggable={false}
              />
            </button>
          ))}
        </div>
      ) : null}
    </div>
  )
}
